import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { API } from 'src/utils/interceptor'
import Sentry from 'src/utils/sentry'

export const checklistScopeKey = (
  venueId,
  supplierId,
  date,
  orderNumber = 'all',
) => JSON.stringify([venueId, supplierId, date || '', orderNumber])

const emptyChecklist = () => ({
  venueSupplier: null,
  venueContact: null,
  approvedOrders: [],
  noApprovedOrders: [],
  updatedOrders: [],
  locked: false,
  orderError: false,
  PONumbersPerDay: [],
  isIntegratedSupplier: false,
})

const applyOrders = (state, data) => {
  state.approvedOrders = data.approvedOrders || []
  state.noApprovedOrders = data.noApprovedOrders || []
  state.PONumbersPerDay = data.PONumbersPerDay || []
  state.venueContact = data.venueContact || null
  state.venueSupplier = data.venueSupplier || null
  state.isIntegratedSupplier = Boolean(data.isIntegratedSupplier)
}

const checklistRequest = createAsyncThunk(
  'checklist/fetch',
  async ({ venueId, supplierId, date, orderNumber }, { rejectWithValue }) => {
    try {
      return await API.get(
        'api',
        `checklist/${venueId}/${supplierId}${
          date ? `/${date}` : ''
        }/${encodeURIComponent(orderNumber)}`,
      )
    } catch (error) {
      const status = error.response?.status || 503
      if (status !== 401 && status !== 403) Sentry.captureException(error)
      return rejectWithValue({ status })
    }
  },
)

const checklistSlice = createSlice({
  name: 'checklist',
  initialState: {
    ...emptyChecklist(),
    isLoading: false,
    accessError: null,
    scopeKey: null,
    viewerId: '',
    requestId: null,
  },
  reducers: {
    setOrders: (state, action) => {
      applyOrders(state, action.payload)
    },
    setOrdersUpdated: (state, action) => {
      state.updatedOrders = action.payload
    },
    lock: state => {
      state.locked = true
    },
    setError: (state, action) => {
      state.orderError = action.payload
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    denyChecklistAccess: (state, action) => {
      Object.assign(state, emptyChecklist())
      state.accessError = action.payload
      state.requestId = null
      state.isLoading = false
    },
  },
  extraReducers: builder => {
    builder
      .addCase(checklistRequest.pending, (state, action) => {
        const {
          venueId,
          supplierId,
          date,
          orderNumber,
          viewerId,
          noShowLoading,
        } = action.meta.arg
        const scopeKey = checklistScopeKey(
          venueId,
          supplierId,
          date,
          orderNumber,
        )
        const changed =
          state.scopeKey !== scopeKey || state.viewerId !== viewerId
        if (changed) Object.assign(state, emptyChecklist())
        state.scopeKey = scopeKey
        state.viewerId = viewerId
        state.requestId = action.meta.requestId
        state.accessError = null
        state.isLoading = changed || !noShowLoading
      })
      .addCase(checklistRequest.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        applyOrders(state, action.payload)
        state.isLoading = false
        state.requestId = null
      })
      .addCase(checklistRequest.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        Object.assign(state, emptyChecklist())
        state.accessError = action.payload || { status: 503 }
        state.isLoading = false
        state.requestId = null
      })
  },
})

export const {
  setOrders,
  lock,
  setError,
  setOrdersUpdated,
  setIsLoading,
  denyChecklistAccess,
} = checklistSlice.actions

export const fetchOrdersChecklist =
  (venueId, supplierId, date, orderNumber = 'all', noShowLoading = false) =>
  (dispatch, getState) =>
    dispatch(
      checklistRequest({
        venueId,
        supplierId,
        date,
        orderNumber,
        noShowLoading,
        viewerId: getState().auth.account?._id || '',
      }),
    )

export const lockOrder =
  (venueId, supplierId, orders, orderError) => async (dispatch, getState) => {
    const { scopeKey, viewerId } = getState().checklist
    const isCurrent = () =>
      getState().checklist.scopeKey === scopeKey &&
      getState().checklist.viewerId === viewerId
    try {
      const data = await API.post('api', `checklist/${venueId}/${supplierId}`, {
        body: orders,
      })
      if (!isCurrent()) return
      dispatch(setOrdersUpdated(data))
      if (orderError) {
        dispatch(setError(false))
      }
      dispatch(lock())
    } catch (e) {
      if (!isCurrent()) return false
      if (e.response?.status === 401 || e.response?.status === 403) {
        dispatch(denyChecklistAccess({ status: e.response.status }))
        return false
      }
      dispatch(lock())
      dispatch(setError(true))
      console.error(e)
      Sentry.captureException(e)
      return false
    }
  }
export const notifications = async (venueId, supplierId) => {
  try {
    await API.post('api', `emailAndPushNotification`, {
      body: {
        venueId,
        supplierId,
      },
    })
  } catch (e) {
    console.log(e)
    return false
  }
}

export const supplierNotifications = async (venueId, supplierId) => {
  try {
    await API.post('api', `emailAndPushNotification`, {
      body: {
        venueId,
        supplierId,
      },
      queryStringParameters: {
        isSupplierMessage: true,
      },
    })
  } catch (e) {
    console.log(e)
    return false
  }
}

export default checklistSlice
